import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Landmark,
  ShieldCheck,
  Target,
  Building2,
  ScrollText,
  CheckCircle2,
  Users,
  Download,
  Share2,
  ClipboardList,
  Church,
  FileText,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";
import { SURVEY_DISCLAIMER, SURVEY_QUESTIONS } from "../data/ncuaSurvey";
import CharterMilestone from "../components/CharterMilestone";
import FCUPreview from "../components/FCUPreview";
import useUTM, { utmPayload } from "../lib/useUTM";

const ZEFFY_URL =
  "https://www.zeffy.com/en-US/ticketing/black-dollars-united-corporations-memberships";

const VIDEO_VERTICAL =
  "/api/static/marketing/trillion_dollar_fcu_announcement.mp4?v=2";
const VIDEO_HORIZONTAL =
  "/api/static/marketing/trillion_dollar_fcu_announcement_horizontal.mp4?v=2";

export const FLYERS = [
  {
    key: "story",
    title: "Instagram Story · TikTok · Reels",
    dims: "1080 × 1920",
    tag: "Vertical",
    url: "/api/static/marketing/credit_union_flyer_story.jpg?v=2",
    filename: "tdspfcu-flyer-story-1080x1920.jpg",
  },
  {
    key: "square",
    title: "Instagram Feed · LinkedIn",
    dims: "1080 × 1080",
    tag: "Square",
    url: "/api/static/marketing/credit_union_flyer_square.jpg?v=2",
    filename: "tdspfcu-flyer-square-1080x1080.jpg",
  },
  {
    key: "landscape",
    title: "Facebook · Twitter · Link Preview",
    dims: "1200 × 630",
    tag: "Landscape",
    url: "/api/static/marketing/credit_union_flyer_landscape.jpg?v=2",
    filename: "tdspfcu-flyer-landscape-1200x630.jpg",
  },
];

const CHARTER_BEATS = [
  {
    icon: ScrollText,
    title: "Pending NCUA Charter Approval",
    body:
      "The proposed Trillion Dollar Spending Power Federal Credit Union is currently in the organizers' phase of application with the National Credit Union Administration. It is not yet operational and cannot accept deposits or open accounts.",
  },
  {
    icon: Target,
    title: "$500,000 Charter Support Goal",
    body:
      "3,000 charter supporters at $100 each. Every pledge of support is held in escrow and used solely toward NCUA charter application costs — never as a deposit.",
  },
  {
    icon: Landmark,
    title: "Escrow At Citizen Trust Bank",
    body:
      "All charter-support contributions sit in a segregated escrow account at Citizen Trust Bank alongside the Trillion Dollar Spending Power Fund. Fully audited. Fully transparent.",
  },
  {
    icon: ShieldCheck,
    title: "Potential Members, On Approval",
    body:
      "If the NCUA grants our charter, every charter supporter will be invited to apply to become a member of the credit union. Until then, the credit union remains proposed, pending NCUA charter approval.",
  },
];

const AMOUNTS = [100, 250, 500, 1000, 2500];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];


function useOrientation() {
  const [isVert, setIsVert] = useState(true);
  return { isVert, setIsVert };
}


function useLiveStats() {
  const [stats, setStats] = useState({
    count: 0,
    goal_members: 3000,
    total_pledged: 0,
    seats_remaining: 3000,
    progress_pct: 0,
  });

  const load = () => {
    api
      .get("/credit-union/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // refresh every 20s
    return () => clearInterval(t);
  }, []);

  return { stats, reload: load };
}


export default function CreditUnion() {
  const { isVert, setIsVert } = useOrientation();
  const { stats, reload: reloadStats } = useLiveStats();
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [payNow, setPayNow] = useState(true);
  const [confirmation, setConfirmation] = useState(null); // subscribe API response

  // Parse ?ref=XXX and ?team=slug from URL — pre-populate the form so share
  // links drop the user straight into a pledge with attribution baked in.
  const [refFromUrl, setRefFromUrl] = useState("");
  const [teamFromUrl, setTeamFromUrl] = useState("");
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setRefFromUrl((p.get("ref") || "").trim().toUpperCase());
    setTeamFromUrl((p.get("team") || "").trim().toLowerCase());
  }, []);

  const activeAmount = useMemo(() => {
    const v = customAmount ? Number(customAmount) : amount;
    return Number.isFinite(v) && v > 0 ? v : 100;
  }, [amount, customAmount]);

  return (
    <div className="grain bg-bdu-bg text-bdu-cream min-h-screen">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/10"
        data-testid="cu-hero-section"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(1200px 500px at 20% 30%, rgba(212,175,55,0.35), transparent 60%), radial-gradient(900px 500px at 80% 70%, rgba(212,175,55,0.15), transparent 60%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-bdu-bg" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left column: copy + CTA */}
          <div className="lg:col-span-6 animate-fade-up">
            <div className="label-eyebrow" data-testid="cu-hero-eyebrow">
              Proposed · Pending NCUA Charter Approval
            </div>
            <h1
              className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-bdu-cream tracking-tight"
              data-testid="cu-hero-headline"
              aria-label="Proposed Trillion Dollar Spending Power Federal Credit Union"
            >
              Trillion Dollar{" "}
              <span className="block mt-2 text-bdu-gold italic">
                Spending Power
              </span>{" "}
              <span className="block mt-2 text-bdu-cream/95 text-4xl sm:text-5xl lg:text-6xl">
                Federal Credit Union
              </span>
            </h1>

            <p className="mt-8 text-lg text-bdu-cream/75 max-w-xl leading-relaxed">
              A proposed community-owned credit union, in the organizers'
              phase of application with the NCUA. Charter approval is
              contingent on raising{" "}
              <span className="text-bdu-gold font-semibold">
                $500,000 in charter-support pledges
              </span>{" "}
              held in escrow at Citizen Trust Bank.
            </p>

            <LiveCounter stats={stats} />

            <div className="mt-10 flex flex-wrap gap-4" data-testid="cu-hero-ctas">
              <a
                href="#subscribe"
                className="btn-gold"
                data-testid="cu-hero-primary-cta"
              >
                Pledge My Charter Support <ArrowRight size={16} />
              </a>
              <a
                href="#charter"
                className="btn-ghost"
                data-testid="cu-hero-secondary-cta"
              >
                See The Charter Plan
              </a>
            </div>
          </div>

          {/* Right column: hero video */}
          <div
            className="lg:col-span-6 animate-fade-up"
            style={{ animationDelay: "150ms" }}
            data-testid="cu-hero-video-wrapper"
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>
                Founder's Announcement
              </div>
              <div
                className="inline-flex items-center gap-1 bg-bdu-surface2 border border-bdu-gold/20 rounded-full p-0.5"
                data-testid="cu-video-toggle"
              >
                <button
                  type="button"
                  onClick={() => setIsVert(false)}
                  className={`px-3 py-1 text-[0.65rem] uppercase tracking-widest transition rounded-full ${
                    !isVert
                      ? "bg-bdu-gold text-black font-semibold"
                      : "text-bdu-cream/60 hover:text-bdu-cream"
                  }`}
                  data-testid="cu-video-toggle-horizontal"
                >
                  Landscape
                </button>
                <button
                  type="button"
                  onClick={() => setIsVert(true)}
                  className={`px-3 py-1 text-[0.65rem] uppercase tracking-widest transition rounded-full ${
                    isVert
                      ? "bg-bdu-gold text-black font-semibold"
                      : "text-bdu-cream/60 hover:text-bdu-cream"
                  }`}
                  data-testid="cu-video-toggle-vertical"
                >
                  Reel
                </button>
              </div>
            </div>

            <div
              className={`relative rounded-lg overflow-hidden ring-1 ring-bdu-gold/30 shadow-[0_30px_80px_-10px_rgba(212,175,55,0.35)] bg-black ${
                isVert ? "aspect-[9/16] max-w-sm mx-auto" : "aspect-video"
              }`}
              data-testid="cu-hero-video-container"
            >
              <video
                key={isVert ? "vert" : "horiz"}
                className="w-full h-full object-contain bg-black"
                src={isVert ? VIDEO_VERTICAL : VIDEO_HORIZONTAL}
                controls
                playsInline
                preload="metadata"
                data-testid="cu-hero-video"
              />
            </div>
            <div className="mt-3 text-center text-xs uppercase tracking-widest text-bdu-cream/40">
              Founder · CEO · President — Greg L Bradford
            </div>
          </div>
        </div>
      </section>

      {/* CHARTER MILESTONE — spectacular dual-ring progress hero */}
      <CharterMilestone />

      {/* FCU PREVIEW — branch signage + VISA debit card showcase */}
      <FCUPreview />

      {/* FLYER — shareable poster */}
      <FlyerSection />

      {/* CHURCH OUTREACH KIT — 7-page print-ready PDF */}
      <ChurchPackSection />

      {/* LEADERBOARD + TEAMS — recruitment viral loop */}
      <RecruitmentSection />

      {/* CHARTER BEATS */}
      <section
        id="charter"
        className="relative border-b border-bdu-gold/10"
        data-testid="cu-charter-beats"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="label-eyebrow">The Charter Application</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
              How the credit union{" "}
              <span className="text-bdu-gold italic">gets chartered</span>
            </h2>
            <p className="mt-4 text-bdu-cream/65 max-w-2xl mx-auto">
              Every charter-support pledge sits in a segregated escrow account until the
              NCUA formally issues our charter number. No charter, no draw.
              Full refund guarantee.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CHARTER_BEATS.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="relative bg-bdu-surface2 border border-bdu-gold/15 p-6 hover:border-bdu-gold/50 transition group"
                data-testid={`cu-beat-${i}`}
              >
                <div className="w-11 h-11 rounded-full bg-bdu-gold/10 border border-bdu-gold/30 flex items-center justify-center text-bdu-gold group-hover:bg-bdu-gold group-hover:text-black transition">
                  <Icon size={20} />
                </div>
                <div className="mt-5 font-serif text-xl text-bdu-cream leading-snug">
                  {title}
                </div>
                <p className="mt-3 text-sm text-bdu-cream/60 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBSCRIPTION FUNNEL */}
      <SubscribeSection
        id="subscribe"
        activeAmount={activeAmount}
        amount={amount}
        setAmount={setAmount}
        customAmount={customAmount}
        setCustomAmount={setCustomAmount}
        payNow={payNow}
        setPayNow={setPayNow}
        stats={stats}
        refFromUrl={refFromUrl}
        teamFromUrl={teamFromUrl}
        onSuccess={(res) => {
          setConfirmation(res);
          reloadStats();
        }}
      />

      {/* FOUNDER */}
      <section className="relative border-b border-bdu-gold/10" data-testid="cu-founder-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="w-full aspect-square bg-bdu-surface2 border border-bdu-gold/25 flex items-center justify-center">
              <Building2 size={92} className="text-bdu-gold/70" />
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="label-eyebrow">Founder · CEO · President</div>
            <h3 className="mt-3 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
              Greg L <span className="text-bdu-gold italic">Bradford</span>
            </h3>
            <p className="mt-6 text-bdu-cream/70 leading-relaxed max-w-2xl">
              Founder, CEO and President of the proposed Trillion Dollar
              Spending Power Federal Credit Union — a Black-owned community
              credit union currently in the organizers' phase of charter
              application with the NCUA, alongside the Trillion Dollar
              Spending Power Fund at Citizen Trust Bank. If the charter is
              granted, every dollar held at the credit union could recirculate
              directly into our own manufacturing plants, grocery stores,
              hospitals, and infrastructure.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/pledge" className="btn-ghost" data-testid="cu-founder-fund-cta">
                Learn About The $50M Fund
              </Link>
              <a
                href="#subscribe"
                className="btn-gold"
                data-testid="cu-founder-subscribe-cta"
              >
                Pledge My Charter Support <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Success modal after subscribe */}
      {confirmation && (
        <ConfirmationModal
          data={confirmation}
          onClose={() => setConfirmation(null)}
        />
      )}

      {/* NCUA COMPLIANCE DISCLAIMER */}
      <section
        className="relative bg-black/60 border-t border-bdu-gold/10"
        data-testid="cu-compliance-disclaimer"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
          <div className="label-eyebrow text-bdu-gold/70" style={{ fontSize: "0.6rem" }}>
            Required disclosure
          </div>
          <p className="mt-3 text-xs text-bdu-cream/55 leading-relaxed">
            The proposed Trillion Dollar Spending Power Federal Credit Union is
            currently in the organizers' phase of application with the National
            Credit Union Administration. It is not yet operational and
            cannot accept deposits or open member accounts. Your pledge of
            charter support is a{" "}
            <strong className="text-bdu-cream/85">
              non-binding expression of interest
            </strong>
            , not a deposit, and does not create a membership relationship.
            Charter-support contributions are held in a segregated escrow
            account at Citizen Trust Bank and used solely toward NCUA
            charter-application costs; if the charter is not granted, all
            contributions are refunded in full. Should the NCUA grant the
            charter, charter supporters will be invited to apply to become
            potential members of the credit union. Until then, the credit
            union remains proposed, pending NCUA charter approval.
          </p>
        </div>
      </section>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Flyer gallery — 3 branded social-media formats, downloadable & shareable
// ---------------------------------------------------------------------------
function FlyerSection() {
  const share = async (flyer) => {
    const shareUrl = `${window.location.origin}/credit-union`;
    const text =
      "Learn how you can support the proposed Trillion Dollar Spending Power Federal Credit Union — a non-binding pledge of charter support, not a deposit. Pending NCUA approval.";
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Proposed Trillion Dollar Spending Power FCU",
          text,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(`${text}\n\n${shareUrl}`);
        toast.success(`${flyer.tag} share text copied to clipboard`);
      }
    } catch (_e) {
      /* user cancelled */
    }
  };

  const downloadAll = () => {
    // Trigger sequential downloads with a tiny stagger so the browser doesn't
    // block them. Filename hints match the platform each flyer targets.
    FLYERS.forEach((f, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = f.url;
        a.download = f.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, i * 400);
    });
    toast.success("Downloading all three flyer sizes…");
  };

  return (
    <section
      className="relative border-b border-bdu-gold/10"
      data-testid="cu-flyer-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-8">
            <div className="label-eyebrow">Social-media assets</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-tight">
              Post it. <span className="text-bdu-gold italic">Print it.</span>{" "}
              Pass it around.
            </h2>
            <p className="mt-5 text-bdu-cream/70 leading-relaxed max-w-2xl">
              Three high-resolution flyers, engineered for every feed. Every
              share brings the charter closer. Post them on your stories, print
              them for your barbershop, salon, or church — anywhere Black
              dollars flow, this message should be seen.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <button
              type="button"
              onClick={downloadAll}
              className="btn-gold"
              data-testid="cu-flyer-download-all-btn"
            >
              <Download size={16} /> Download All 3 Sizes
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-testid="cu-flyer-grid">
          {FLYERS.map((f) => (
            <FlyerCard key={f.key} flyer={f} onShare={() => share(f)} />
          ))}
        </div>

        <ul
          className="mt-14 grid sm:grid-cols-3 gap-4 text-xs text-bdu-cream/55 border-t border-bdu-gold/10 pt-8"
          data-testid="cu-flyer-benefits"
        >
          <li className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-bdu-gold flex-shrink-0" />
            High-resolution & retina-crisp
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-bdu-gold flex-shrink-0" />
            Live scannable QR to /credit-union
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-bdu-gold flex-shrink-0" />
            Prints beautifully at 8.5×11 or 11×17
          </li>
        </ul>
      </div>
    </section>
  );
}


// ---------- Church Outreach Kit ----------

const CHURCH_PACK_URL = `${API}/credit-union/outreach/church-pack.pdf`;

const CHURCH_PACK_CONTENTS = [
  { icon: FileText, label: "Pastor's Welcome Letter", sub: "Full-page serif letter with scripture hook & QR" },
  { icon: Printer, label: "Sanctuary / Foyer Flyer", sub: "Full-page letter · prints clean at 8.5×11" },
  { icon: Printer, label: "Half-Page Flyers (2-up)", sub: "Usher hand-outs · cut on dashed line" },
  { icon: ClipboardList, label: "Pew / Prayer Cards (10-up)", sub: "Business-card size · slip in bulletin or purse" },
  { icon: FileText, label: "Bulletin Insert Strips (3-up)", sub: "Fits inside Sunday order of service" },
  { icon: Users, label: "Fellowship Hall Sign-Up Sheet", sub: "20 rows · name · phone · email · pledge" },
  { icon: Share2, label: "60-Second Talking Points", sub: "What to say when passing it around" },
];

function ChurchPackSection() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Trigger the download via an anchor to let the browser handle it reliably.
      const a = document.createElement("a");
      a.href = CHURCH_PACK_URL;
      a.download = "BDU_Church_Outreach_Kit.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Church Outreach Kit — your 7-page PDF is on its way");
    } catch (e) {
      toast.error("Download failed. Please try again.");
    } finally {
      setTimeout(() => setDownloading(false), 900);
    }
  };

  const handlePreview = () => {
    window.open(CHURCH_PACK_URL + "?inline=true", "_blank", "noopener,noreferrer");
  };

  return (
    <section
      className="relative border-b border-bdu-gold/10"
      data-testid="cu-church-pack-section"
      style={{
        background:
          "radial-gradient(1100px 500px at 15% 0%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 60%)," +
          "radial-gradient(900px 500px at 85% 100%, rgba(227,38,54,0.08) 0%, rgba(0,0,0,0) 60%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: pitch */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-bdu-gold/40 text-bdu-gold text-[10px] uppercase tracking-[0.3em]">
              <Church size={12} /> Church Outreach Kit · #6
            </div>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05] tracking-tight">
              Take it to the <span className="text-bdu-gold italic">pulpit.</span>
              <br />
              Pass it through the <span className="text-bdu-gold italic">pews.</span>
            </h2>
            <p className="mt-6 text-bdu-cream/70 leading-relaxed max-w-xl">
              A seven-page print-ready PDF built for Black church outreach.
              Pastor's letter, sanctuary flyer, bulletin inserts, pew cards,
              a sign-up sheet, and talking points for ushers — all with
              NCUA-compliant language and a scannable QR to pledge.
            </p>

            {/* Scripture pull-quote */}
            <blockquote
              className="mt-8 relative border-l-2 border-bdu-gold/50 pl-5 py-2 max-w-xl"
              data-testid="cu-church-pack-scripture"
            >
              <p className="font-serif italic text-xl text-bdu-cream/85 leading-relaxed">
                "A good man leaveth an inheritance to his children's children."
              </p>
              <footer className="mt-2 text-xs uppercase tracking-[0.26em] text-bdu-gold">
                Proverbs 13:22
              </footer>
            </blockquote>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="btn-gold px-7 py-3.5 disabled:opacity-70 disabled:cursor-wait"
                data-testid="cu-church-pack-download-btn"
              >
                <Download size={16} />
                {downloading ? "Preparing PDF…" : "Download Church Pack (PDF)"}
              </button>
              <button
                type="button"
                onClick={handlePreview}
                className="btn-ghost px-7 py-3.5"
                data-testid="cu-church-pack-preview-btn"
              >
                Preview in New Tab
              </button>
            </div>

            {/* NCUA footnote */}
            <p
              className="mt-6 text-xs text-bdu-cream/50 italic max-w-xl leading-relaxed"
              data-testid="cu-church-pack-compliance"
            >
              Every page carries the proposed-credit-union disclosure. Pledges
              are non-binding expressions of intent, held in escrow at Citizen
              Trust Bank, refundable in full if the NCUA charter is not granted.
            </p>
          </div>

          {/* Right: what's inside */}
          <div className="lg:col-span-5">
            <div
              className="relative border border-bdu-gold/25 bg-black/60 backdrop-blur-sm p-8"
              data-testid="cu-church-pack-contents-card"
              style={{
                boxShadow:
                  "0 40px 80px -30px rgba(212,175,55,0.3), inset 0 1px 0 rgba(212,175,55,0.06)",
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-bdu-gold">
                    What's Inside
                  </div>
                  <div className="mt-1 font-serif text-2xl text-bdu-cream">
                    7 pages · letter-size
                  </div>
                </div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center border border-bdu-gold/40">
                  <FileText size={22} className="text-bdu-gold" />
                </div>
              </div>

              <div className="mt-6 w-full h-px bg-bdu-gold/15" />

              <ul className="mt-6 space-y-4" data-testid="cu-church-pack-contents-list">
                {CHURCH_PACK_CONTENTS.map((item, i) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3 animate-fade-up"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center border border-bdu-gold/40 bg-bdu-gold/5">
                      <item.icon size={13} className="text-bdu-gold" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-bdu-cream">
                        {item.label}
                      </div>
                      <div className="text-xs text-bdu-cream/55 leading-snug">
                        {item.sub}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-7 pt-6 border-t border-bdu-gold/15 flex items-center justify-between text-[11px] uppercase tracking-[0.26em] text-bdu-cream/55">
                <span>Print B&amp;W or color</span>
                <span className="text-bdu-gold">Letter · 8.5×11</span>
              </div>
            </div>

            {/* Mini usage tips */}
            <ul className="mt-6 grid grid-cols-3 gap-3 text-[11px] text-bdu-cream/55">
              <li className="flex items-start gap-2">
                <CheckCircle2 size={12} className="text-bdu-gold flex-shrink-0 mt-0.5" />
                Pass in fellowship hall
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={12} className="text-bdu-gold flex-shrink-0 mt-0.5" />
                Insert in Sunday bulletin
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={12} className="text-bdu-gold flex-shrink-0 mt-0.5" />
                Scannable QR to pledge
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}




export function FlyerCard({ flyer, onShare }) {
  const aspectClass =
    flyer.key === "story"
      ? "aspect-[9/16]"
      : flyer.key === "square"
      ? "aspect-square"
      : "aspect-[1200/630]";

  return (
    <div
      className="group border border-bdu-gold/20 bg-bdu-surface2 p-3 hover:border-bdu-gold/60 transition"
      data-testid={`cu-flyer-card-${flyer.key}`}
    >
      <a
        href={flyer.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`block relative overflow-hidden ring-1 ring-bdu-gold/20 ${aspectClass} bg-black`}
        aria-label={`Open ${flyer.tag} flyer full size`}
        data-testid={`cu-flyer-image-link-${flyer.key}`}
      >
        <img
          src={flyer.url}
          alt={`${flyer.title} — Proposed Trillion Dollar Spending Power FCU flyer`}
          className="w-full h-full object-cover transition group-hover:scale-[1.02]"
          data-testid={`cu-flyer-image-${flyer.key}`}
          loading="lazy"
        />
        <div className="absolute top-2 left-2 bg-bdu-gold text-black text-[0.6rem] uppercase tracking-widest font-semibold px-2 py-1">
          {flyer.tag}
        </div>
      </a>
      <div className="px-1 pt-4 pb-1">
        <div className="text-sm font-serif text-bdu-cream leading-tight">
          {flyer.title}
        </div>
        <div className="mt-1 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40">
          {flyer.dims} · JPG
        </div>
        <div className="mt-4 flex gap-2">
          <a
            href={flyer.url}
            download={flyer.filename}
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-[0.65rem] uppercase tracking-widest bg-bdu-gold text-black font-semibold hover:brightness-110 transition"
            data-testid={`cu-flyer-download-${flyer.key}`}
          >
            <Download size={13} /> Download
          </a>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-[0.65rem] uppercase tracking-widest border border-bdu-gold/40 text-bdu-cream hover:border-bdu-gold transition"
            data-testid={`cu-flyer-share-${flyer.key}`}
            aria-label={`Share ${flyer.tag} flyer`}
          >
            <Share2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Recruitment — public leaderboard + team pledging section
// ---------------------------------------------------------------------------
function RecruitmentSection() {
  const [leaders, setLeaders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showTeamForm, setShowTeamForm] = useState(false);

  useEffect(() => {
    api.get("/credit-union/leaderboard?limit=10").then((r) => setLeaders(r.data.rows || [])).catch(() => {});
    api.get("/credit-union/teams?limit=10").then((r) => setTeams(r.data.rows || [])).catch(() => {});
  }, []);

  const TIER_BADGE = {
    PLATINUM: { label: "PLATINUM", cls: "bg-bdu-cream text-black" },
    GOLD: { label: "GOLD", cls: "bg-bdu-gold text-black" },
    SILVER: { label: "SILVER", cls: "bg-bdu-cream/70 text-black" },
    BRONZE: { label: "BRONZE", cls: "bg-[#CD7F32] text-black" },
  };

  return (
    <section
      className="relative border-b border-bdu-gold/10"
      data-testid="cu-recruitment-section"
      style={{ backgroundColor: "#07070A" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-14">
        {/* Leaderboard */}
        <div data-testid="cu-leaderboard">
          <div className="label-eyebrow">Top charter recruiters</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight">
            Climb the <span className="text-bdu-gold italic">roll of honor</span>.
          </h2>
          <p className="mt-3 text-sm text-bdu-cream/60 max-w-md">
            Every supporter you bring in moves us closer to the charter — and
            lifts your name up the leaderboard. 3 refers earn Bronze · 10
            Silver · 25 Gold · 50 Platinum.
          </p>
          <div className="mt-7 border border-bdu-gold/15" data-testid="cu-leaderboard-table">
            {leaders.length === 0 ? (
              <div className="p-8 text-center text-bdu-cream/40 text-sm">
                Be the first to recruit — your name goes here.
              </div>
            ) : (
              leaders.map((row, i) => (
                <div
                  key={row.referral_code}
                  className="flex items-center justify-between gap-4 px-5 py-4 border-b border-bdu-gold/10 last:border-b-0"
                  data-testid={`cu-leaderboard-row-${row.referral_code}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="font-serif text-bdu-gold text-xl tabular-nums w-7">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-bdu-cream truncate text-sm">{row.name}</div>
                      <div className="text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 mt-0.5">
                        {row.count} refers · ${Number(row.pledged || 0).toLocaleString()} pledged
                      </div>
                    </div>
                  </div>
                  {row.tier && (
                    <span className={`text-[0.6rem] uppercase tracking-widest px-2 py-1 font-semibold ${TIER_BADGE[row.tier]?.cls}`}>
                      {TIER_BADGE[row.tier]?.label}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Teams */}
        <div data-testid="cu-teams">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-eyebrow">Pledge as a team</div>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight">
                Churches · HBCUs · <span className="text-bdu-gold italic">Greek letter</span>.
              </h2>
              <p className="mt-3 text-sm text-bdu-cream/60 max-w-md">
                Rally your congregation, chapter, or alumni group. Team
                captains get a share link; every pledge made under that link
                counts toward the team roster.
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowTeamForm((v) => !v)}
              className="btn-gold text-xs"
              data-testid="cu-team-create-toggle"
            >
              {showTeamForm ? "Cancel" : "Start A Team"}
            </button>
          </div>

          {showTeamForm && (
            <CreateTeamForm
              onCreated={(team) => {
                setShowTeamForm(false);
                api.get("/credit-union/teams?limit=10").then((r) => setTeams(r.data.rows || []));
                toast.success(`Team created · share link ready`);
                const url = `${window.location.origin}/credit-union?team=${team.slug}`;
                navigator.clipboard?.writeText(url);
                toast.info("Share link copied to clipboard");
              }}
            />
          )}

          <div className="mt-7 border border-bdu-gold/15" data-testid="cu-teams-table">
            {teams.length === 0 ? (
              <div className="p-8 text-center text-bdu-cream/40 text-sm">
                No teams yet. Be the first captain.
              </div>
            ) : (
              teams.map((t, i) => {
                const shareUrl = `${window.location.origin}/credit-union?team=${t.slug}`;
                return (
                  <div
                    key={t.slug}
                    className="flex items-center justify-between gap-4 px-5 py-4 border-b border-bdu-gold/10 last:border-b-0"
                    data-testid={`cu-team-row-${t.slug}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="font-serif text-bdu-gold text-xl tabular-nums w-7">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-bdu-cream truncate text-sm">{t.name}</div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 mt-0.5">
                          {t.type} · {t.count} supporters · ${Number(t.pledged || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(shareUrl);
                        toast.success(`Copied ${t.name}'s share link`);
                      }}
                      className="text-[0.6rem] uppercase tracking-widest text-bdu-gold hover:text-bdu-cream"
                      data-testid={`cu-team-copy-${t.slug}`}
                    >
                      Copy Link
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


function CreateTeamForm({ onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("church");
  const [captain, setCaptain] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !captain.trim() || !email.trim()) {
      toast.error("Name, captain, and email are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/credit-union/teams", {
        name: name.trim(),
        type,
        captain_name: captain.trim(),
        captain_email: email.trim().toLowerCase(),
        bio: bio.trim() || null,
      });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create team");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-bdu-gold/25 px-3 py-2 text-sm text-bdu-cream outline-none focus:border-bdu-gold placeholder-bdu-cream/30";

  return (
    <form
      onSubmit={submit}
      className="mt-5 border border-bdu-gold/25 bg-bdu-surface2 p-5 space-y-3"
      data-testid="cu-team-form"
    >
      <input className={inputCls} placeholder="Team name (e.g., Ebenezer Baptist Church)" value={name} onChange={(e) => setName(e.target.value)} data-testid="cu-team-name" />
      <div className="grid grid-cols-2 gap-3">
        <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="cu-team-type">
          <option value="church">Church</option>
          <option value="hbcu">HBCU / Alumni</option>
          <option value="greek">Fraternity / Sorority</option>
          <option value="business">Business / Chamber</option>
          <option value="other">Other</option>
        </select>
        <input className={inputCls} placeholder="Captain name" value={captain} onChange={(e) => setCaptain(e.target.value)} data-testid="cu-team-captain" />
      </div>
      <input type="email" className={inputCls} placeholder="Captain email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cu-team-email" />
      <textarea className={`${inputCls} min-h-[70px]`} placeholder="Short bio (optional)" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="cu-team-bio" />
      <button type="submit" disabled={busy} className="btn-gold text-xs" data-testid="cu-team-submit">
        {busy ? "Creating…" : "Create Team"}
      </button>
    </form>
  );
}


// ---------------------------------------------------------------------------
// Live counter card
// ---------------------------------------------------------------------------
function LiveCounter({ stats }) {
  const pct = Math.min(100, Number(stats.progress_pct || 0));
  return (
    <div
      className="mt-10 border border-bdu-gold/25 bg-bdu-surface/60 backdrop-blur px-6 py-5 max-w-xl"
      data-testid="cu-live-counter"
    >
      <div className="flex items-center gap-2 text-bdu-gold">
        <Users size={16} />
        <span
          className="label-eyebrow"
          style={{ fontSize: "0.6rem" }}
          data-testid="cu-live-counter-label"
        >
          Live · Charter Supporters
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-3 flex-wrap">
        <span
          className="font-serif text-4xl text-bdu-cream tabular-nums"
          data-testid="cu-live-counter-count"
        >
          {Number(stats.count || 0).toLocaleString()}
        </span>
        <span className="text-sm text-bdu-cream/60">
          of{" "}
          {Number(stats.goal_members || 3000).toLocaleString()}{" "}
          charter supporters
        </span>
        <span className="text-sm text-bdu-cream/40">·</span>
        <span
          className="text-sm text-bdu-gold tabular-nums"
          data-testid="cu-live-counter-pledged"
        >
          ${Number(stats.total_pledged || 0).toLocaleString()} pledged
        </span>
      </div>
      <div className="mt-4 h-1.5 w-full bg-bdu-gold/10 overflow-hidden">
        <div
          className="h-full bg-bdu-gold transition-[width] duration-1000"
          style={{ width: `${pct}%` }}
          data-testid="cu-live-counter-bar"
        />
      </div>
      <div className="mt-2 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40">
        {Number(stats.seats_remaining || 0).toLocaleString()} supporter slots remaining
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Subscription funnel
// ---------------------------------------------------------------------------
function SubscribeSection({
  id,
  activeAmount,
  amount,
  setAmount,
  customAmount,
  setCustomAmount,
  payNow,
  setPayNow,
  stats,
  refFromUrl,
  teamFromUrl,
  onSuccess,
}) {
  const utm = useUTM();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    dob: "",
    ssn_last4: "",
    address: "",
    city: "",
    state: "GA",
    zip_code: "",
    signature_agreed: false,
  });
  // Survey: multi-selects as arrays, single-selects as strings. Keyed by SURVEY_QUESTIONS.
  const [survey, setSurvey] = useState(() =>
    SURVEY_QUESTIONS.reduce(
      (acc, q) => ({ ...acc, [q.key]: q.type === "checkbox" ? [] : "" }),
      {}
    )
  );
  const toggleSurvey = (q, option) => {
    setSurvey((s) => {
      if (q.type === "checkbox") {
        const cur = s[q.key] || [];
        return {
          ...s,
          [q.key]: cur.includes(option) ? cur.filter((x) => x !== option) : [...cur, option],
        };
      }
      return { ...s, [q.key]: option };
    });
  };
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [fomStatus, setFomStatus] = useState(null); // { in_fom, county }

  // Live FOM check — hit the API after the user types a 5-digit ZIP so they
  // see their eligibility in real time. Debounced via a short timeout.
  useEffect(() => {
    const z = (form.zip_code || "").replace(/\D/g, "").slice(0, 5);
    if (z.length !== 5) {
      setFomStatus(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get(`/credit-union/fom/check?zip=${z}`)
        .then((r) => {
          if (!cancelled) setFomStatus(r.data);
        })
        .catch(() => {
          if (!cancelled) setFomStatus(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.zip_code]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name || form.full_name.trim().length < 2) e.full_name = "Enter your legal full name";
    if (!/^\S+@\S+\.\S+$/.test(form.email || "")) e.email = "Enter a valid email";
    if (!form.phone || form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone number";
    if (!form.dob) e.dob = "Date of birth is required";
    if (form.ssn_last4 && !/^\d{4}$/.test(form.ssn_last4)) e.ssn_last4 = "Must be exactly 4 digits";
    if (!form.address || form.address.trim().length < 3) e.address = "Enter your street address";
    if (!form.city) e.city = "City is required";
    if (!form.state) e.state = "State is required";
    if (!form.zip_code || form.zip_code.trim().length < 3) e.zip_code = "ZIP is required";
    if (!(Number(activeAmount) > 0)) e.pledge_amount = "Pick or enter a pledge amount";
    if (!form.signature_agreed) e.signature_agreed = "You must agree to the charter-support terms";
    // NCUA survey — every question is required
    SURVEY_QUESTIONS.forEach((q) => {
      const v = survey[q.key];
      if (q.type === "checkbox") {
        if (!Array.isArray(v) || v.length < (q.minSelected || 1)) {
          e[`survey_${q.key}`] = "Please select at least one option";
        }
      } else if (!v) {
        e[`survey_${q.key}`] = "Please choose one";
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/credit-union/subscribe", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        dob: form.dob,
        ssn_last4: form.ssn_last4 || null,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip_code: form.zip_code.trim(),
        pledge_amount: Number(activeAmount),
        signature_agreed: true,
        pay_now: !!payNow,
        referral_code: refFromUrl || null,
        team_slug: teamFromUrl || null,
        survey_fom_relationship: survey.fom_relationship,
        survey_current_bank: survey.current_bank,
        survey_join_likelihood: survey.join_likelihood,
        survey_primary_reason: survey.primary_reason,
        survey_deposit_accounts: survey.deposit_accounts,
        survey_deposit_balance: survey.deposit_balance,
        survey_loan_products: survey.loan_products,
        survey_loan_balance: survey.loan_balance,
        survey_delivery_channels: survey.delivery_channels,
        survey_bank_satisfaction: survey.bank_satisfaction,
        ...utmPayload(utm),
      });
      toast.success(`Pledge received · Supporter ID ${data.member_number}`);
      onSuccess(data);
      // Reset form so counter can climb but form is empty
      setForm({
        full_name: "",
        email: "",
        phone: "",
        dob: "",
        ssn_last4: "",
        address: "",
        city: "",
        state: "GA",
        zip_code: "",
        signature_agreed: false,
      });
      setSurvey(
        SURVEY_QUESTIONS.reduce(
          (acc, q) => ({ ...acc, [q.key]: q.type === "checkbox" ? [] : "" }),
          {}
        )
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold text-sm placeholder-bdu-cream/30";
  const label = (t) => (
    <div className="label-eyebrow mb-2" style={{ fontSize: "0.6rem" }}>
      {t}
    </div>
  );
  const errCls = (k) =>
    errors[k] ? "border-bdu-red/60 focus:border-bdu-red" : "";

  return (
    <section
      id={id}
      className="relative border-b border-bdu-gold/10"
      style={{ backgroundColor: "#050505" }}
      data-testid="cu-subscribe-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(900px 500px at 50% 50%, rgba(212,175,55,0.20), transparent 65%)",
        }}
      />
      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 py-24">
        <div className="text-center">
          <div className="label-eyebrow">Charter-Support Pledge</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
            Pledge your <span className="text-bdu-gold italic">charter</span> support
          </h2>
          <p className="mt-4 text-bdu-cream/65 max-w-2xl mx-auto">
            One page. Ninety seconds. You get an instant supporter ID, a
            downloadable charter-supporter card, and your name on the roll
            of charter supporters engraved on the charter certificate.
          </p>
          <p className="mt-3 text-xs text-bdu-cream/40 max-w-2xl mx-auto">
            This pledge is a non-binding expression of interest in supporting
            the charter application — not a deposit and not an application
            for membership.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-14 bg-bdu-surface2 border border-bdu-gold/25 p-6 sm:p-10 space-y-10"
          data-testid="cu-subscribe-form"
          noValidate
        >
          {(refFromUrl || teamFromUrl) && (
            <div
              className="border border-bdu-gold/40 bg-bdu-gold/5 px-4 py-3 flex items-start gap-3 text-xs"
              data-testid="cu-attribution-banner"
            >
              <CheckCircle2 size={14} className="text-bdu-gold mt-0.5 flex-shrink-0" />
              <div className="text-bdu-cream/80 leading-relaxed">
                {refFromUrl && (
                  <>You were invited by <strong className="text-bdu-gold">{refFromUrl}</strong>. </>
                )}
                {teamFromUrl && (
                  <>Pledging as part of team <strong className="text-bdu-gold">{teamFromUrl}</strong>. </>
                )}
                Your pledge will give them credit on the leaderboard.
              </div>
            </div>
          )}

          {/* STEP 1 — Pledge */}
          <div>
            <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
              Step 1 · Choose your pledge
            </div>
            <div
              className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3"
              data-testid="cu-pledge-amounts"
            >
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => {
                    setAmount(a);
                    setCustomAmount("");
                  }}
                  className={`py-3.5 border transition text-center font-serif text-lg ${
                    amount === a && !customAmount
                      ? "bg-bdu-gold text-black border-bdu-gold"
                      : "bg-transparent text-bdu-cream border-bdu-gold/20 hover:border-bdu-gold/60"
                  }`}
                  data-testid={`cu-pledge-amount-${a}`}
                >
                  ${a.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="mt-5">
              <label
                className="label-eyebrow block"
                style={{ fontSize: "0.6rem" }}
                htmlFor="cu-custom-amount"
              >
                Or enter a custom amount ($)
              </label>
              <input
                id="cu-custom-amount"
                type="number"
                min="25"
                step="25"
                placeholder="e.g. 750"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className={`${inputCls} mt-2 max-w-xs`}
                data-testid="cu-pledge-custom-amount"
              />
            </div>
          </div>

          {/* STEP 2 — Identity */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
                Step 2 · Your info
              </div>
            </div>
            <div className="sm:col-span-2">
              {label("Legal Full Name")}
              <input
                value={form.full_name}
                onChange={set("full_name")}
                className={`${inputCls} ${errCls("full_name")}`}
                placeholder="First Middle Last"
                data-testid="cu-input-fullname"
                autoComplete="name"
              />
              {errors.full_name && (
                <div className="mt-1 text-xs text-bdu-red/80" data-testid="cu-err-fullname">
                  {errors.full_name}
                </div>
              )}
            </div>
            <div>
              {label("Email")}
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                className={`${inputCls} ${errCls("email")}`}
                placeholder="you@email.com"
                data-testid="cu-input-email"
                autoComplete="email"
              />
              {errors.email && (
                <div className="mt-1 text-xs text-bdu-red/80" data-testid="cu-err-email">
                  {errors.email}
                </div>
              )}
            </div>
            <div>
              {label("Phone")}
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className={`${inputCls} ${errCls("phone")}`}
                placeholder="(555) 555-0100"
                data-testid="cu-input-phone"
                autoComplete="tel"
              />
              {errors.phone && (
                <div className="mt-1 text-xs text-bdu-red/80">{errors.phone}</div>
              )}
            </div>
            <div>
              {label("Date of Birth")}
              <input
                type="date"
                value={form.dob}
                onChange={set("dob")}
                className={`${inputCls} ${errCls("dob")}`}
                data-testid="cu-input-dob"
                autoComplete="bday"
              />
              {errors.dob && (
                <div className="mt-1 text-xs text-bdu-red/80">{errors.dob}</div>
              )}
            </div>
            <div>
              {label("SSN Last 4 (optional)")}
              <input
                inputMode="numeric"
                maxLength={4}
                value={form.ssn_last4}
                onChange={(e) => setForm({ ...form, ssn_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                className={`${inputCls} ${errCls("ssn_last4")}`}
                placeholder="1234"
                data-testid="cu-input-ssn4"
              />
              <div className="mt-1 text-[0.65rem] text-bdu-cream/40">
                Speeds up NCUA verification. Never shared, encrypted at rest.
              </div>
              {errors.ssn_last4 && (
                <div className="mt-1 text-xs text-bdu-red/80">{errors.ssn_last4}</div>
              )}
            </div>
            <div className="sm:col-span-2">
              {label("Residential Address")}
              <input
                value={form.address}
                onChange={set("address")}
                className={`${inputCls} ${errCls("address")}`}
                placeholder="123 Main St, Apt 4B"
                data-testid="cu-input-address"
                autoComplete="street-address"
              />
              {errors.address && (
                <div className="mt-1 text-xs text-bdu-red/80">{errors.address}</div>
              )}
            </div>
            <div>
              {label("City")}
              <input
                value={form.city}
                onChange={set("city")}
                className={`${inputCls} ${errCls("city")}`}
                placeholder="Atlanta"
                data-testid="cu-input-city"
                autoComplete="address-level2"
              />
              {errors.city && <div className="mt-1 text-xs text-bdu-red/80">{errors.city}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                {label("State")}
                <select
                  value={form.state}
                  onChange={set("state")}
                  className={`${inputCls} ${errCls("state")}`}
                  data-testid="cu-input-state"
                  autoComplete="address-level1"
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                {label("ZIP")}
                <input
                  value={form.zip_code}
                  onChange={set("zip_code")}
                  className={`${inputCls} ${errCls("zip_code")}`}
                  placeholder="30303"
                  data-testid="cu-input-zip"
                  autoComplete="postal-code"
                />
                {errors.zip_code && (
                  <div className="mt-1 text-xs text-bdu-red/80">{errors.zip_code}</div>
                )}
              </div>
            </div>
          </div>

          {/* FOM eligibility chip */}
          {fomStatus && (
            <div
              className={`mt-4 flex items-start gap-3 border px-4 py-3 ${
                fomStatus.in_fom
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-bdu-gold/30 bg-bdu-gold/5"
              }`}
              data-testid={`cu-fom-status-${fomStatus.in_fom ? "in" : "out"}`}
            >
              <div
                className={`mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  fomStatus.in_fom ? "bg-emerald-400" : "bg-bdu-gold"
                }`}
              />
              <div className="text-xs leading-relaxed">
                {fomStatus.in_fom ? (
                  <>
                    <strong className="text-emerald-400 uppercase tracking-widest text-[0.65rem]">
                      In proposed FOM · {fomStatus.county}
                    </strong>
                    <div className="mt-1 text-bdu-cream/70">
                      Your ZIP sits inside the proposed Field of Membership
                      (Fulton · DeKalb · College Park, GA). Your pledge
                      strengthens our NCUA Field-of-Membership Study.
                    </div>
                  </>
                ) : (
                  <>
                    <strong className="text-bdu-gold uppercase tracking-widest text-[0.65rem]">
                      Outside proposed FOM
                    </strong>
                    <div className="mt-1 text-bdu-cream/70">
                      Our initial charter targets Fulton · DeKalb · College Park, GA.
                      You can still pledge your support — once chartered, we
                      plan to expand to surrounding communities and we'll
                      invite you first.
                    </div>
                  </>
                )}
                <Link
                  to="/field-of-membership"
                  className="inline-flex items-center gap-1 mt-2 text-[0.65rem] uppercase tracking-widest text-bdu-gold hover:text-bdu-cream transition-colors"
                  data-testid="cu-fom-see-map-link"
                >
                  See the full FOM map &amp; directory →
                </Link>
              </div>
            </div>
          )}

          {/* STEP 3 — NCUA Potential Member Survey (required for Form 9500 charter application) */}
          <div className="border-t border-bdu-gold/15 pt-10" data-testid="cu-survey-section">
            <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
              Step 3 · NCUA Potential Member Survey
            </div>
            <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-bdu-cream leading-tight">
              Ten quick questions — the <span className="italic text-bdu-gold">evidence</span> NCUA needs.
            </h3>
            <div className="mt-3 flex items-start gap-2 border border-bdu-gold/20 bg-bdu-gold/5 px-4 py-3 text-xs text-bdu-cream/70 leading-relaxed">
              <ClipboardList size={14} className="text-bdu-gold mt-0.5 flex-shrink-0" />
              <span>{SURVEY_DISCLAIMER}</span>
            </div>

            <div className="mt-6 space-y-7">
              {SURVEY_QUESTIONS.map((q) => {
                const value = survey[q.key];
                const errKey = `survey_${q.key}`;
                return (
                  <div key={q.key} data-testid={`cu-survey-q-${q.key}`}>
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif text-bdu-gold text-2xl tabular-nums w-8">
                        {q.number}.
                      </span>
                      <span className="text-sm text-bdu-cream leading-snug">
                        {q.label}
                      </span>
                    </div>
                    <div className="mt-3 pl-11 space-y-2.5">
                      {q.options.map((opt) => {
                        const selected =
                          q.type === "checkbox"
                            ? Array.isArray(value) && value.includes(opt)
                            : value === opt;
                        return (
                          <label
                            key={opt}
                            className={`flex items-start gap-3 cursor-pointer border px-3 py-2.5 transition ${
                              selected
                                ? "border-bdu-gold bg-bdu-gold/5"
                                : "border-bdu-gold/15 hover:border-bdu-gold/40"
                            }`}
                            data-testid={`cu-survey-opt-${q.key}-${opt
                              .replace(/[^a-z0-9]+/gi, "-")
                              .toLowerCase()
                              .slice(0, 40)}`}
                          >
                            <input
                              type={q.type}
                              name={q.key}
                              checked={selected}
                              onChange={() => toggleSurvey(q, opt)}
                              className="mt-0.5 accent-bdu-gold flex-shrink-0"
                            />
                            <span className="text-xs sm:text-sm text-bdu-cream/85 leading-relaxed">
                              {opt}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {errors[errKey] && (
                      <div
                        className="pl-11 mt-1 text-xs text-bdu-red/80"
                        data-testid={`cu-survey-err-${q.key}`}
                      >
                        {errors[errKey]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* STEP 4 — Payment option */}
          <div>
            <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
              Step 4 · Lock it in
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="cu-pay-toggle">
              <button
                type="button"
                onClick={() => setPayNow(true)}
                className={`text-left p-5 border transition ${
                  payNow
                    ? "bg-bdu-gold/10 border-bdu-gold"
                    : "border-bdu-gold/20 hover:border-bdu-gold/50"
                }`}
                data-testid="cu-pay-toggle-now"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full border ${
                      payNow ? "bg-bdu-gold border-bdu-gold" : "border-bdu-gold/50"
                    }`}
                  />
                  <span className="text-sm font-serif text-bdu-cream">
                    Pledge support + Contribute ${Number(activeAmount || 100).toLocaleString()} to charter costs
                  </span>
                </div>
                <p className="mt-2 text-xs text-bdu-cream/60 leading-relaxed">
                  Fee-free through Zeffy. Higher placement on the charter-supporter roll.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setPayNow(false)}
                className={`text-left p-5 border transition ${
                  !payNow
                    ? "bg-bdu-gold/10 border-bdu-gold"
                    : "border-bdu-gold/20 hover:border-bdu-gold/50"
                }`}
                data-testid="cu-pay-toggle-later"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full border ${
                      !payNow ? "bg-bdu-gold border-bdu-gold" : "border-bdu-gold/50"
                    }`}
                  />
                  <span className="text-sm font-serif text-bdu-cream">
                    Pledge support only (contribute later)
                  </span>
                </div>
                <p className="mt-2 text-xs text-bdu-cream/60 leading-relaxed">
                  Record your pledge now. We'll email a fee-free contribution link.
                </p>
              </button>
            </div>
          </div>

          {/* STEP 5 — Signature agreement */}
          <div>
            <label
              className="flex items-start gap-3 cursor-pointer"
              data-testid="cu-signature-agreement"
            >
              <input
                type="checkbox"
                checked={form.signature_agreed}
                onChange={set("signature_agreed")}
                className="mt-1 w-4 h-4 accent-bdu-gold"
                data-testid="cu-input-signature"
              />
              <span className="text-sm text-bdu-cream/80 leading-relaxed">
                I acknowledge that the proposed Trillion Dollar Spending Power
                Federal Credit Union is pending NCUA charter approval and is
                not yet operational. My pledge is a{" "}
                <strong className="text-bdu-cream">
                  non-binding expression of interest
                </strong>
                , not a deposit or application for membership. Any contribution
                I make is held in escrow at Citizen Trust Bank solely for
                charter-application costs and is fully refundable if the
                charter is not granted. By entering my name{form.full_name ? " " : ""}
                <strong className="text-bdu-gold">
                  {form.full_name ? `— ${form.full_name}` : ""}
                </strong>{" "}
                I agree to be added to the charter-supporter roster.
              </span>
            </label>
            {errors.signature_agreed && (
              <div
                className="mt-2 text-xs text-bdu-red/80"
                data-testid="cu-err-signature"
              >
                {errors.signature_agreed}
              </div>
            )}
          </div>

          <div className="pt-2 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold"
              data-testid="cu-submit-btn"
            >
              {submitting
                ? "Recording your pledge…"
                : payNow
                ? `Pledge Support & Contribute $${Number(activeAmount || 100).toLocaleString()}`
                : `Record My Pledge Of Support`}{" "}
              <ArrowRight size={16} />
            </button>
            <div className="text-xs text-bdu-cream/50">
              {Number(stats.seats_remaining || 0).toLocaleString()} of{" "}
              {Number(stats.goal_members || 3000).toLocaleString()} charter-supporter
              slots remaining
            </div>
          </div>

          <ul className="pt-6 border-t border-bdu-gold/10 space-y-2 text-xs text-bdu-cream/60">
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="mt-0.5 text-bdu-gold flex-shrink-0" />
              Every contribution held in escrow at Citizen Trust Bank
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="mt-0.5 text-bdu-gold flex-shrink-0" />
              Full refund guarantee if NCUA charter is not granted
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 size={14} className="mt-0.5 text-bdu-gold flex-shrink-0" />
              Your charter-supporter card ships to your inbox in seconds
            </li>
          </ul>
        </form>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Confirmation modal
// ---------------------------------------------------------------------------
function ConfirmationModal({ data, onClose }) {
  const [payClicked, setPayClicked] = useState(false);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const openZeffy = () => {
    if (data.zeffy_url) {
      window.open(data.zeffy_url, "_blank", "noopener,noreferrer");
      setPayClicked(true);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="cu-confirmation-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md border border-bdu-gold/50 bg-bdu-surface2 p-8 sm:p-10 shadow-[0_30px_100px_-10px_rgba(212,175,55,0.4)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-bdu-cream/50 hover:text-bdu-cream text-xl leading-none"
          aria-label="Close"
          data-testid="cu-confirmation-close"
        >
          ×
        </button>
        <div className="label-eyebrow text-bdu-gold" data-testid="cu-confirmation-label">
          Pledge of support received
        </div>
        <h3 className="mt-3 font-serif text-3xl text-bdu-cream leading-tight">
          Thank you,{" "}
          <span className="text-bdu-gold italic">{data.full_name}</span>
        </h3>
        <div
          className="mt-6 border border-bdu-gold/30 p-4"
          data-testid="cu-confirmation-details"
        >
          <div className="flex justify-between items-baseline">
            <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
              Supporter ID
            </span>
            <span
              className="font-mono text-bdu-gold text-lg"
              data-testid="cu-confirmation-member-number"
            >
              {data.member_number}
            </span>
          </div>
          <div className="mt-3 flex justify-between items-baseline border-t border-bdu-gold/10 pt-3">
            <span className="label-eyebrow" style={{ fontSize: "0.6rem" }}>
              Pledge Amount
            </span>
            <span className="font-serif text-xl text-bdu-cream">
              ${Number(data.pledge_amount || 0).toLocaleString()}
            </span>
          </div>
        </div>
        <p className="mt-6 text-sm text-bdu-cream/70 leading-relaxed">
          Your charter-supporter card is on the way to your inbox — save it
          to your phone as proof of your pledge of support.
        </p>

        {data.my_referral_code && (
          <div
            className="mt-6 border border-bdu-gold/40 bg-bdu-gold/5 p-4"
            data-testid="cu-confirmation-ref"
          >
            <div className="label-eyebrow text-bdu-gold" style={{ fontSize: "0.6rem" }}>
              Your personal share link
            </div>
            <div className="mt-2 font-serif text-2xl text-bdu-cream tracking-wider">
              {data.my_referral_code}
            </div>
            <div className="mt-2 font-mono text-[0.7rem] text-bdu-gold break-all" data-testid="cu-confirmation-ref-url">
              {typeof window !== "undefined" ? window.location.origin : ""}
              /credit-union?ref={data.my_referral_code}
            </div>
            <button
              type="button"
              onClick={() => {
                const url = `${window.location.origin}/credit-union?ref=${data.my_referral_code}`;
                navigator.clipboard?.writeText(url);
                toast.success("Share link copied!");
              }}
              className="btn-ghost w-full mt-3 text-xs"
              data-testid="cu-confirmation-copy-ref"
            >
              Copy My Share Link
            </button>
            <div className="mt-3 text-[0.65rem] text-bdu-cream/50 leading-relaxed">
              Every supporter you bring in moves us closer to the charter.
              3 refers = Bronze · 10 = Silver · 25 = Gold · 50 = Platinum.
            </div>
          </div>
        )}

        {data.my_referral_code && (
          <div
            className="mt-6 relative overflow-hidden border border-bdu-gold/50 bg-gradient-to-br from-black via-bdu-surface2 to-black p-5"
            data-testid="cu-confirmation-ig-panel"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{
                background:
                  "radial-gradient(300px 180px at 50% 0%, rgba(212,175,55,0.6) 0%, rgba(0,0,0,0) 60%)",
              }}
            />
            <div className="relative">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="label-eyebrow text-bdu-gold" style={{ fontSize: "0.6rem" }}>
                  📸  Share this to your Instagram Story
                </div>
                <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50">
                  1080 × 1920 · ready to post
                </div>
              </div>
              <p className="mt-2 text-xs text-bdu-cream/60 leading-relaxed">
                Every repost = free reach. The QR on the card leads straight to your personal share link.
              </p>
              <div className="mt-4 flex items-start gap-4">
                <a
                  href={`${API}/credit-union/story-card/${data.my_referral_code}.png`}
                  target="_blank"
                  rel="noreferrer"
                  className="block flex-shrink-0 border border-bdu-gold/40 rounded-sm overflow-hidden hover:border-bdu-gold transition group"
                  style={{ width: 112, aspectRatio: "9 / 16" }}
                  data-testid="cu-confirmation-ig-thumb-link"
                >
                  <img
                    src={`${API}/credit-union/story-card/${data.my_referral_code}.png`}
                    alt="Your Instagram story card"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                    data-testid="cu-confirmation-ig-thumb"
                  />
                </a>
                <div className="flex-1 min-w-0 space-y-2">
                  <a
                    href={`${API}/credit-union/story-card/${data.my_referral_code}.png`}
                    download={`BDU_Pledge_${data.member_number || "Story"}.png`}
                    className="btn-gold w-full justify-center text-xs"
                    data-testid="cu-confirmation-ig-download"
                  >
                    Download Story Image
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      const url = `${API}/credit-union/story-card/${data.my_referral_code}.png`;
                      const shareText = `I just signed Pledge #${(data.member_number || "").split("-").pop().replace(/^0+/, "")} for the Proposed Trillion Dollar Spending Power Federal Credit Union. Join me:`;
                      const shareUrl = `${window.location.origin}/credit-union?ref=${data.my_referral_code}&utm_source=instagram&utm_medium=story&utm_campaign=supporter_share`;
                      try {
                        // Prefer native share with image file when supported (iOS Safari + modern Android)
                        if (navigator.canShare && navigator.share) {
                          const resp = await fetch(url);
                          const blob = await resp.blob();
                          const file = new File([blob], `BDU_Pledge_Story.png`, { type: "image/png" });
                          if (navigator.canShare({ files: [file] })) {
                            await navigator.share({ files: [file], title: "Join me", text: shareText, url: shareUrl });
                            return;
                          }
                        }
                        // Fallback: copy a share-worthy caption to clipboard
                        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                        toast.success("Caption copied — paste it in your IG story!");
                      } catch (_) {
                        toast.message("Save the image, then post it in your IG story.");
                      }
                    }}
                    className="btn-ghost w-full justify-center text-xs"
                    data-testid="cu-confirmation-ig-share"
                  >
                    Share to Instagram
                  </button>
                  <div className="text-[0.6rem] text-bdu-cream/40 leading-snug">
                    Tip: tap the thumbnail to preview full size.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {data.zeffy_url && !payClicked && (
          <button
            type="button"
            onClick={openZeffy}
            className="btn-gold w-full mt-6"
            data-testid="cu-confirmation-pay-btn"
          >
            Contribute to Charter Costs <ArrowRight size={16} />
          </button>
        )}
        {payClicked && (
          <div
            className="mt-6 text-xs text-bdu-cream/60 text-center"
            data-testid="cu-confirmation-payopened"
          >
            Zeffy opened in a new tab. Return here after your contribution.
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost w-full mt-3"
          data-testid="cu-confirmation-done-btn"
        >
          {data.zeffy_url && !payClicked ? "I\u2019ll Contribute Later" : "Done"}
        </button>
      </div>
    </div>
  );
}
